import axios from "axios";
import { Fragment, useEffect, useState } from "react";
import MapView from "../MapView";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-hot-toast";
import { FaChevronDown } from "react-icons/fa6";
import { useUserContext } from "../../context/UserContext";
import DeviceIdInput from "../DeviceIdInput";

const locations = ["development", "production"];

export default function DeviceConfig() {
  // ...existing state
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const { token, deviceId: device_id } = useUserContext();
  const [deviceId, setDeviceId] = useState(device_id);
  const [datasetLocation, setDatasetLocation] = useState(locations[0]);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [numberAttached, setNumberAttached] = useState(availableNumbers[0]);
  const [isDatasetDropdownOpen, setIsDatasetDropdownOpen] = useState(false);
  const [isNumberDropdownOpen, setIsNumberDropdownOpen] = useState(false);
  const [minMaxValues, setMinMaxValues] = useState({});
  const [sensordata, setSensordata] = useState({});
  const [instanceId, setInstanceId] = useState("");
  const [scenarioCode, setScenarioCode] = useState("");
  const [recheckValue, setRecheckValue] = useState("");

  useEffect(() => {
    if (!token) return;
    if (!deviceId || deviceId.length < 12) return;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/deviceconfig/attached-number/${deviceId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.status !== 200) throw new Error(res.data.message);
        setAvailableNumbers(res.data.data);
        setNumberAttached(res.data.data[0]);
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.message ?? err.message);
      }
    })();

    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/deviceconfig/get-specific-values/${deviceId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setMinMaxValues(res.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setMinMaxValues(err.response.data.defaults);
          return;
        }
        console.error(err);
        toast.error(err.response?.data?.message ?? err.message);
      }
    })();
  }, [deviceId, token]);

  useEffect(() => {
    if (!token) return;
    if (!deviceId || deviceId.length < 12) return;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/deviceconfig/get-last-sensor-data/${deviceId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        delete res.data.createdAt;
        delete res.data.updatedAt;
        res.data.timestamp = new Date(res.data.timestamp).toLocaleString(
          "en-GB",
        );
        setSensordata(res.data);
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.message ?? err.message);
      }
    })();
  }, [deviceId, token]);

  function changeDatasetLocation(datasetLocation) {
    setDatasetLocation(datasetLocation);
    setIsDatasetDropdownOpen(false);
  }
  function attachNumber(num) {
    setNumberAttached(num);
    setIsNumberDropdownOpen(false);
  }
  function attachMobileNumber() {
    if (!token) return;
    if (!deviceId || deviceId.trim() === "" || deviceId.length < 12) {
      alert("Enter Valid Device ID");
      return;
    }
    if (!numberAttached) {
      alert("Select a number");
      return;
    }
    (async () => {
      try {
        let res = await axios.post(
          `${process.env.REACT_APP_API_URL}/deviceconfig/transfer-device`,
          {
            device_id: deviceId,
            new_customer_mobile_number: numberAttached,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.status !== 200) throw new Error(res.data.message);
        toast.success("Number attached successfully");
      } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.message ?? e.message);
      }
    })();
  }
  async function saveMinMaxValues(e) {
    e.preventDefault();
    if (!token) return;
    const formElements = e.target.elements;
    let emergency = {};
    let master = {};
    for (let i = 0; i < formElements.length; i++) {
      const element = formElements[i];
      if (element.name === "" || element.value.trim() === "") continue;
      if (element.getAttribute("data-type") === "master")
        master[element.name] = Number(element.value);
      else if (element.getAttribute("data-type") === "emergency")
        emergency[element.name] = Number(element.value);
    }
    if (Object.keys(emergency).length === 0 && Object.keys(master).length === 0)
      return toast.error("No updated value found");

    emergency = { ...minMaxValues.emergency, ...emergency };
    master = { ...minMaxValues.master, ...master };
    try {
      let res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/deviceconfig/update-specific-values`,
        {
          emergency,
          master,
          device_id: deviceId,
          deviceOnly: false,
          changeInBackup: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.status !== 200) throw new Error(res.data.message);
      toast.success("Min Max Values Saved successfully");
      setMinMaxValues({ emergency, master });
      e.target.reset();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message ?? e.message);
    }
  }

  async function sendRecheckRequest(){
    if (!deviceId || deviceId.trim() === "" || deviceId.length < 12) {
      toast.error("Enter Valid Device ID");
      return;
    }
    try{
      let res = await axios.post(
        `${process.env.REACT_APP_API_URL}/deviceconfig/recheck-device`,
        {
          device_id: deviceId,
          instance_id: instanceId,
          scenario_code: scenarioCode,
          recheck_value: recheckValue,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.status !== 200) throw new Error(res.data.message);
      toast.success("Recheck Request Sent successfully");
    }catch(e){
      console.error(e);
      toast.error(e.response?.data?.message ?? e.message);
    }
  }

  async function setDeviceDefaultOn(){
    if (!deviceId || deviceId.trim() === "" || deviceId.length < 12) {
      toast.error("Enter Valid Device ID");
      return;
    }
    try{
      let res = await axios.post(
        `${process.env.REACT_APP_API_URL}/deviceconfig/default-on`,
        {
          device_id: deviceId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.status !== 200) throw new Error(res.data.message);
      toast.success("Device Default On Set successfully");
    }catch(e){
      console.error(e);
      toast.error(e.response?.data?.message ?? e.message);
    }
  }

  async function factoryReset() {
    if (!deviceId || deviceId.trim() === "" || deviceId.length < 12) {
      toast.error("Enter Valid Device ID");
      return;
    }
    confirmAlert({
      title: "Confirm",
      message: "Are you sure you want to reset this device?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            factoryResetDevice();
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
    async function factoryResetDevice() {
      try {
        let res = await axios.post(
          `${process.env.REACT_APP_API_URL}/deviceconfig/factory-reset`,
          {
            device_id: deviceId,
            factory_reset: 0,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.status !== 200) throw new Error(res.data.message);
        toast.success("Device factory reset successfully");
      } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.message ?? e.message);
      }
    }
  }

  return (
    <div className="flex flex-col w-full min-h-full min-w-0 px-3 py-6 sm:px-6 md:px-8 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-center w-full">
        <DeviceIdInput deviceId={deviceId} setDeviceId={setDeviceId} />
      </div>
      {deviceId && deviceId.length >= 12 && (
        <div className="w-full space-y-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
              Change Dataset Location and MQTT URL
            </h2>
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-stretch md:items-end p-4 sm:p-6 bg-white rounded-xl shadow-md border border-gray-100">
              <div id="datasetDropdown" className="relative w-full md:w-auto min-w-[160px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
                <button
                  type="button"
                  onClick={() => setIsDatasetDropdownOpen(!isDatasetDropdownOpen)}
                  className="flex gap-2 justify-between items-center py-2 px-4 w-full font-semibold border-2 border-gray-300 rounded-lg hover:border-indigo-500 bg-white"
                >
                  <span className="capitalize">{datasetLocation}</span>
                  <FaChevronDown className="text-xs text-gray-500" />
                </button>
                <ul
                  className="absolute z-20 left-0 right-0 p-2 mt-1 bg-white rounded-lg divide-y divide-gray-100 shadow-xl border border-gray-200 cursor-pointer"
                  style={{ display: isDatasetDropdownOpen ? "block" : "none" }}
                >
                  {locations.map((loc, i) => (
                    <li
                      key={i}
                      className="py-2 px-3 hover:bg-indigo-50 hover:text-indigo-600 rounded text-sm transition-colors"
                      onClick={() => changeDatasetLocation(loc)}
                    >
                      {loc}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">Full MQTT URL</label>
                <input
                  type="url"
                  placeholder="mqtts://mqtt-dev.komsup.com:8883/"
                  className="py-2 px-3 w-full text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                />
              </div>
              <button className="py-2.5 px-6 text-base font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-colors shrink-0">
                SEND
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">Set Device Default On</h2>
            <div className="p-4 sm:p-6 bg-white rounded-xl shadow-md border border-gray-100">
              <button
                onClick={setDeviceDefaultOn}
                className="w-full sm:w-auto py-2.5 px-6 text-base font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
              >
                Set Default On
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">Reboot Device</h2>
            <div className="p-4 sm:p-6 bg-white rounded-xl shadow-md border border-gray-100">
              <button
                onClick={async () => {
                  if (!deviceId) {
                    toast.error('Please enter a device ID');
                    return;
                  }
                  confirmAlert({
                    title: 'Confirm Reboot',
                    message: 'Are you sure you want to reboot this device?',
                    buttons: [
                      {
                        label: 'Yes',
                        onClick: async () => {
                          try {
                            const response = await axios.post(
                              `${process.env.REACT_APP_API_URL}/deviceconfig/reboot`,
                              { device_id: deviceId, reboot: 1 },
                              {
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`,
                                },
                              }
                            );
                            if (response.status === 200) {
                              toast.success('Device reboot initiated successfully');
                            } else {
                              throw new Error(response.data?.message || 'Failed to initiate reboot');
                            }
                          } catch (error) {
                            console.error('Reboot error:', error);
                            toast.error(error.response?.data?.message || 'Failed to reboot device');
                          }
                        },
                      },
                      {
                        label: 'No',
                        onClick: () => {},
                      },
                    ],
                  });
                }}
                className="w-full sm:w-auto py-2.5 px-6 text-base font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors"
              >
                Reboot Device
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">Send Recheck Request</h2>
            <div className="p-4 sm:p-6 bg-white rounded-xl shadow-md border border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <input
                  type="text"
                  value={instanceId}
                  onChange={(e) => setInstanceId(e.target.value)}
                  placeholder="Instance ID"
                  className="py-2 px-3 w-full text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                />
                <input
                  type="text"
                  value={scenarioCode}
                  onChange={(e) => setScenarioCode(e.target.value)}
                  placeholder="Scenario Code"
                  className="py-2 px-3 w-full text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                />
                <input
                  type="text"
                  value={recheckValue}
                  onChange={(e) => setRecheckValue(e.target.value)}
                  placeholder="Recheck Value"
                  className="py-2 px-3 w-full text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                />
              </div>
              <button
                onClick={sendRecheckRequest}
                className="w-full sm:w-auto mt-4 py-2.5 px-6 text-base font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
              >
                Send Recheck Request
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">Reset device to factory settings</h2>
            <div className="p-4 sm:p-6 bg-white rounded-xl shadow-md border border-gray-100">
              <button
                onClick={factoryReset}
                className="w-full sm:w-auto py-2.5 px-6 text-base font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors"
              >
                Factory Reset
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">Attach mobile number to device</h2>
            <div className="p-4 sm:p-6 bg-white rounded-xl shadow-md border border-gray-100">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
                <span className="text-sm sm:text-base font-medium text-gray-700 sm:w-28 shrink-0">Mobile No:</span>
                <div className="relative flex-1 min-w-0 max-w-sm">
                  <input
                    type="tel"
                    className="w-full py-2 px-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                    value={numberAttached}
                    placeholder="Enter or select number"
                    onFocus={() => setIsNumberDropdownOpen(true)}
                    onChange={(e) => setNumberAttached(e.target.value)}
                  />
                  {isNumberDropdownOpen && (
                    <ul className="absolute z-20 left-0 right-0 p-2 mt-1 max-h-60 overflow-y-auto bg-white rounded-lg divide-y divide-gray-100 shadow-xl border border-gray-200 cursor-pointer">
                      {availableNumbers
                        .filter((num) => num.includes(numberAttached))
                        .map((num, i) => (
                          <li
                            key={i}
                            className="py-2 px-3 hover:bg-indigo-50 hover:text-indigo-600 rounded text-sm transition-colors"
                            onClick={() => attachNumber(num)}
                          >
                            {num}
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
                <button
                  className="w-full sm:w-auto py-2.5 px-6 text-base font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-colors shrink-0"
                  onClick={attachMobileNumber}
                >
                  Attach
                </button>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">Change location of device</h2>
            <div className="p-4 sm:p-6 bg-white rounded-xl shadow-md border border-gray-100">
              <div className="w-full overflow-hidden rounded-lg">
                <MapView
                  latitude={latitude}
                  longitude={longitude}
                  onMapClick={({ lat, lng }) => {
                    setLatitude(lat);
                    setLongitude(lng);
                  }}
                />
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:gap-6 sm:items-center justify-between">
                <div className="flex flex-wrap gap-2 sm:gap-4 text-sm font-medium text-gray-700">
                  <span className="bg-gray-100 px-3 py-1.5 rounded-md">Latitude: <strong className="text-gray-900">{latitude ?? '--'}</strong></span>
                  <span className="bg-gray-100 px-3 py-1.5 rounded-md">Longitude: <strong className="text-gray-900">{longitude ?? '--'}</strong></span>
                </div>
                <button
                  className="w-full sm:w-auto py-2.5 px-6 text-base font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-colors shrink-0"
                  onClick={async () => {
                    if (!latitude || !longitude) {
                      alert('Please select a location on the map.');
                      return;
                    }
                    try {
                      const response = await fetch(`${process.env.REACT_APP_API_URL}/deviceconfig/location-change`, {
                        method: "POST",
                        headers: {
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          device_id: deviceId,
                          location: { latitude, longitude }
                        })
                      });
                      console.log(response);
                      if (response.ok) {
                        toast.success('Device location changed successfully!');
                      } else {
                        const data = await response.json();
                        toast.error(data.message || 'Failed to change device location.');
                      }
                    } catch (err) {
                      toast.error('Network error. Please try again.');
                    }
                  }}
                >
                  Change Location
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <form onSubmit={saveMinMaxValues} className="w-full min-w-0">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">Set Min Max Values</h2>
              <div className="p-4 sm:p-6 bg-white rounded-xl shadow-md border border-gray-100 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Emergency</h3>
                  <div className="overflow-x-auto w-full">
                    <div className="min-w-[340px] space-y-2">
                      <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider pb-1 border-b">
                        <span className="col-span-5">Parameter</span>
                        <span className="col-span-3 text-center">Current</span>
                        <span className="col-span-4 text-center">Set Value</span>
                      </div>
                      {Object.keys(minMaxValues.emergency ?? Object()).map((key, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
                          <span className="col-span-5 truncate font-medium text-gray-700" title={key}>{key}</span>
                          <input
                            readOnly
                            value={minMaxValues.emergency[key]}
                            className="col-span-3 p-1.5 px-2 text-center border rounded bg-gray-100 text-gray-600 text-sm"
                          />
                          <input
                            className="col-span-4 p-1.5 px-2 text-center border-2 border-gray-300 rounded focus:outline-none focus:border-indigo-500 text-sm"
                            data-type="emergency"
                            name={key}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Master</h3>
                  <div className="overflow-x-auto w-full">
                    <div className="min-w-[340px] space-y-2">
                      <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider pb-1 border-b">
                        <span className="col-span-5">Parameter</span>
                        <span className="col-span-3 text-center">Current</span>
                        <span className="col-span-4 text-center">Set Value</span>
                      </div>
                      {Object.keys(minMaxValues.master ?? Object()).map((key, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
                          <span className="col-span-5 truncate font-medium text-gray-700" title={key}>{key}</span>
                          <input
                            value={minMaxValues.master[key]}
                            readOnly
                            className="col-span-3 p-1.5 px-2 text-center border rounded bg-gray-100 text-gray-600 text-sm"
                          />
                          <input
                            className="col-span-4 p-1.5 px-2 text-center border-2 border-gray-300 rounded focus:outline-none focus:border-indigo-500 text-sm"
                            data-type="master"
                            name={key}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full sm:w-auto py-2.5 px-6 text-base font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
                >
                  Save Min-Max Values
                </button>
              </div>
            </form>

            <div className="w-full min-w-0">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">Latest Sensor Data</h2>
              <div className="p-4 sm:p-6 bg-white rounded-xl shadow-md border border-gray-100">
                <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                  {Object.entries(sensordata).length === 0 ? (
                    <p className="text-gray-500 py-4 text-sm">No sensor data available</p>
                  ) : (
                    Object.entries(sensordata).map(([key, value], i) => (
                      <div key={i} className="flex justify-between py-2 text-sm gap-2">
                        <span className="font-medium text-gray-600 break-words">{key}</span>
                        <span className="text-gray-900 font-mono text-right break-all">{value ? value.toString() : ''}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
